import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Play } from 'lucide-react';
import { HeroSectionContent, PageSectionProps } from '@/types/cms';
import { cn } from '@/lib/utils';

interface HeroSectionProps extends PageSectionProps {
  content: HeroSectionContent;
}

const HeroSection: React.FC<HeroSectionProps> = ({ 
  section, 
  content, 
  className,
  isPreview = false
}) => {
  const {
    subtitle,
    description,
    cta_text = 'Get Started',
    cta_link = '#',
    background_type = 'minimal',
    background_image,
    background_video,
    background_color
  } = content as HeroSectionContent;

  // Helper function to get responsive class prefix
  const getResponsivePrefix = () => isPreview ? '@' : '';

  const backgroundStyles = React.useMemo(() => {
    switch (background_type) {
      case 'image':
        return background_image
          ? { backgroundImage: `url(${background_image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : {};
      case 'color':
        return { backgroundColor: background_color || '#f8fafc' };
      case 'gradient':
        return {
          background: 'linear-gradient(to bottom, hsl(var(--primary) / 0.05) 0%, hsl(var(--primary) / 0.03) 70%, hsl(var(--primary) / 0.01) 100%)',
        };
      case 'minimal':
        return { backgroundColor: 'hsl(var(--background))' };
      default:
        return { backgroundColor: 'hsl(var(--background))' };
    }
  }, [background_type, background_image, background_color]);

  return (
    <section 
      className={cn(
        "relative flex flex-col justify-center overflow-hidden pt-12 pb-16",
        `${getResponsivePrefix()}lg:pt-16`,
        cta_text ? "min-h-[60vh]" : "min-h-[30vh]", // Smaller height when no CTA
        className
      )}
      style={backgroundStyles}
    >
      {/* Background Video */}
      {background_type === 'video' && background_video && (
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={background_video} type="video/mp4" />
        </video>
      )}

      {/* Overlay for better text readability */}
      {(background_type === 'image' || background_type === 'video') && (
        <div className="absolute inset-0 bg-black/40" />
      )}

      {/* Decorative elements for gradient background (matching Index.tsx) */}
      {background_type === 'gradient' && (
        <>
          <div 
            className="absolute top-1/4 right-1/4 w-32 h-32 rounded-full blur-xl"
            style={{
              backgroundColor: 'hsl(var(--primary) / 0.08)'
            }}
          />
          <div 
            className="absolute bottom-1/3 left-1/6 w-24 h-24 rounded-full blur-lg"
            style={{
              backgroundColor: 'hsl(var(--primary) / 0.06)'
            }}
          />
        </>
      )}

      {/* Content - Flex container */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 text-center flex-1 flex flex-col justify-center">
        <div className="max-w-4xl mx-auto">
          {/* Main Title */}
          <h1 className={cn(
            `text-4xl ${getResponsivePrefix()}md:text-6xl font-bold mb-6 leading-tight`,
            (background_type === 'image' || background_type === 'video') 
              ? "text-white" 
              : "text-foreground"
          )}>
            {section.title}
          </h1>

          {/* Subtitle */}
          {subtitle && (
            <p className={cn(
              `text-xl ${getResponsivePrefix()}md:text-2xl mb-8 font-medium`,
              (background_type === 'image' || background_type === 'video') 
                ? "text-white/90" 
                : "text-muted-foreground"
            )}>
              {subtitle}
            </p>
          )}

          {/* Description */}
          {description && (
            <p className={cn(
              `text-lg ${getResponsivePrefix()}md:text-xl leading-relaxed max-w-3xl mx-auto`,
              cta_text ? "mb-12" : "mb-0", // Only add bottom margin if there's a CTA button
              (background_type === 'image' || background_type === 'video') 
                ? "text-white/80" 
                : "text-muted-foreground"
            )}>
              {description}
            </p>
          )}

          {/* CTA Button */}
          {cta_text && (
            <div className={`flex flex-col ${getResponsivePrefix()}sm:flex-row gap-4 justify-center items-center`}>
              <Button 
                asChild
                size="lg"
                className="min-w-[200px] h-12 text-lg font-semibold"
              >
                <a href={cta_link}>
                  {cta_text}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>

              {/* Optional secondary action for video */}
              {background_type === 'video' && (
                <Button 
                  variant="outline" 
                  size="lg"
                  className={cn(
                    "min-w-[200px] h-12 text-lg font-semibold",
                    "bg-white/10 border-white/30 text-white hover:bg-white/20"
                  )}
                >
                  <Play className="mr-2 h-5 w-5" />
                  Watch Story
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;